import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import { useT } from '../i18n/i18n';
import { download, logoUrl, refreshSchool, uploadLogo, type School } from './schoolApi';

export default function SchoolSettingsTab({ school }: { school: School }) {
  const t = useT();
  const [name, setName] = useState(school.name);
  const [ai, setAi] = useState(school.aiEnabled);
  const [parents2fa, setParents2fa] = useState(school.parentsTwoFactor);
  const [logo, setLogo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState('');

  useEffect(() => { setName(school.name); setAi(school.aiEnabled); setParents2fa(school.parentsTwoFactor); }, [school.name, school.aiEnabled, school.parentsTwoFactor]);

  useEffect(() => {
    let url: string | null = null;
    let live = true;
    if (school.hasLogo) {
      void logoUrl().then((u) => { url = u; if (live) setLogo(u); });
    } else {
      setLogo(null);
    }
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [school.hasLogo]);

  async function run(action: () => Promise<unknown>, done: string) {
    setError(null);
    setSaved(null);
    setBusy(true);
    try {
      await action();
      refreshSchool();
      setSaved(done);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That did not work. Please try again.'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  function save(e: FormEvent) {
    e.preventDefault();
    void run(() => api('/school/settings', { method: 'PATCH', token: true, body: { name: name.trim(), aiEnabled: ai, parentsTwoFactor: parents2fa } }), t('Saved.'));
  }

  async function erase(e: FormEvent) {
    e.preventDefault();
    const ok = await run(() => api('/school/erase', { method: 'POST', token: true, body: { confirm } }), t('Every pupil and everything recorded about them has been deleted.'));
    if (ok) setConfirm('');
  }

  return (
    <div className="spaced">
      {error && <div className="error" role="alert">{error}</div>}
      {saved && <div className="banner" role="status">{saved}</div>}

      <form className="card spaced" onSubmit={save}>
        <h3>{t('School')}</h3>
        <div className="field">
          <label htmlFor="school-name">{t('School name')}</label>
          <input id="school-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label className="check">
            <input type="checkbox" checked={ai} onChange={(e) => setAi(e.target.checked)} />
            {t('Allow EasyLearning AI and outside experts')}
          </label>
          <span className="field-hint">
            {t('Off by default for schools. When on, staff can ask EasyLearning AI for help and book outside experts; what they choose to share about a pupil leaves the school\'s records. Check this with your data protection lead first.')}
          </span>
        </div>
        <div className="field">
          <label className="check">
            <input type="checkbox" checked={parents2fa} onChange={(e) => setParents2fa(e.target.checked)} />
            {t('Parents must use two-step sign-in')}
          </label>
          <span className="field-hint">
            {t('Parents sign in with an email and password. When this is on, each parent sets up a code from an app on their phone before they can see their child. Staff who sign in with the school\'s own account are protected by your school\'s sign-in rules.')}
          </span>
        </div>
        <div className="toolbar">
          <button type="submit" className="btn" disabled={busy || name.trim().length < 2}>{t('Save')}</button>
        </div>
      </form>

      <div className="card spaced">
        <h3>{t('Logo')}</h3>
        {logo ? <img src={logo} alt={t('The school logo')} className="school-logo-preview" /> : <p className="muted">{t('No logo yet.')}</p>}
        <div className="field">
          <label htmlFor="school-logo">{t('Choose a picture')}</label>
          <input
            id="school-logo"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void run(() => uploadLogo(file), t('The logo has been changed.'));
            }}
          />
          <span className="field-hint">{t('PNG, JPEG or WebP, under 256 KB. Staff see it at the top of the School page.')}</span>
        </div>
        {school.hasLogo && (
          <div className="toolbar">
            <button type="button" className="btn ghost" disabled={busy} onClick={() => void run(() => api('/school/logo', { method: 'DELETE', token: true }), t('The logo has been removed.'))}>
              {t('Remove logo')}
            </button>
          </div>
        )}
      </div>

      <div className="card spaced">
        <h3>{t('Your school\'s data')}</h3>
        <p>{t('Download everything EasyLearning holds for the school: pupils, classes, staff, practice records, plans, pictures and the audit log.')}</p>
        <div className="toolbar">
          <button
            type="button"
            className="btn secondary"
            disabled={busy}
            onClick={() => void run(() => download('/school/export', `school-data-${new Date().toISOString().slice(0, 10)}.zip`), t('The download has started.'))}
          >
            {t('Download all school data')}
          </button>
        </div>
      </div>

      <form className="card spaced" onSubmit={erase}>
        <h3>{t('Delete all pupil data')}</h3>
        <p>{t('Deletes every pupil, everything recorded about them, and every class. Staff accounts and the audit log stay. This cannot be undone.')}</p>
        <div className="field">
          <label htmlFor="school-erase">{t('Type the school name to confirm: {name}', { name: school.name })}</label>
          <input id="school-erase" value={confirm} autoComplete="off" onChange={(e) => setConfirm(e.target.value)} />
        </div>
        <div className="toolbar">
          <button type="submit" className="btn danger" disabled={busy || confirm.trim().toLowerCase() !== school.name.toLowerCase()}>
            {t('Delete all pupil data')}
          </button>
        </div>
      </form>
    </div>
  );
}
